import { createContext, ReactNode, useContext, useState, useMemo, useCallback } from 'react';
import AutoRenewalModal from '@/enterprise/components/AutoRenewalModal';
import { TakerMarket } from '@/enterprise/data/useTakerMarkets';
import DeleteScheduleModal from '../automation/components/DeleteScheduleModal';
import OfferModal, { OfferModalAccordionValue, OfferModalReferrer } from './OfferModal';

interface OfferModalContextType {
  accordionValue: OfferModalAccordionValue;
  closeModal: () => void;
  isOpen: boolean;
  openModal: ({
    takerMarket,
    initialAccordionValue,
    referrer,
  }: {
    takerMarket: TakerMarket;
    initialAccordionValue?: OfferModalAccordionValue;
    referrer?: OfferModalReferrer;
  }) => void;
  takerMarket: TakerMarket | null;
  openDeleteScheduleModal: (takerMarket: TakerMarket) => void;
  closeDeleteScheduleModal: () => void;
  isOpenDeleteScheduleModal: boolean;
  isOpenAutoRenewalModal: boolean;
  closeAutoRenewalModal: () => void;
  openAutoRenewalModal: (takerMarket: TakerMarket) => void;
  offerModalReferrer: OfferModalReferrer;
}

interface OfferModalProviderProps {
  children: ReactNode;
}

const OfferModalContext = createContext<OfferModalContextType | undefined>(undefined);

export const useOfferModalContext = () => {
  const context = useContext(OfferModalContext);

  if (!context) {
    throw new Error('useOfferModalContext must be used within an OfferModalProvider');
  }

  return context;
};

const OfferModalProvider = ({ children }: OfferModalProviderProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isOpenDeleteScheduleModal, setIsOpenDeleteScheduleModal] = useState(false);
  const [isOpenAutoRenewalModal, setIsOpenAutoRenewalModal] = useState(false);
  const [takerMarket, setTakerMarket] = useState<TakerMarket | null>(null);
  const [accordionValue, setAccordionValue] = useState<OfferModalAccordionValue>('');
  const [offerModalReferrer, setOfferModalReferrer] = useState<OfferModalReferrer | undefined>(undefined);

  const openModal = useCallback(
    ({
      takerMarket: tm,
      initialAccordionValue = '',
      referrer = 'offer',
    }: {
      takerMarket: TakerMarket;
      initialAccordionValue?: OfferModalAccordionValue;
      referrer?: OfferModalReferrer;
    }) => {
      setTakerMarket(tm);
      setAccordionValue(initialAccordionValue);
      setOfferModalReferrer(referrer);
      setIsOpen(true);
    },
    []
  );

  const clearTakerMarket = useCallback(() => {
    setTimeout(() => {
      setTakerMarket(null);
      setOfferModalReferrer(undefined);
    }, 300);
  }, []);

  const closeModal = useCallback(() => {
    setIsOpen(false);
    // clear takerMarket after modal is closed
    clearTakerMarket();
  }, [clearTakerMarket]);

  const closeDeleteScheduleModal = useCallback(() => {
    setIsOpenDeleteScheduleModal(false);
    // clear takerMarket after modal is closed
    clearTakerMarket();
  }, [clearTakerMarket]);

  const openDeleteScheduleModal = useCallback((tm: TakerMarket) => {
    setTakerMarket(tm);
    setIsOpenDeleteScheduleModal(true);
  }, []);

  const openAutoRenewalModal = useCallback((tm: TakerMarket) => {
    setTakerMarket(tm);
    setIsOpenAutoRenewalModal(true);
  }, []);

  const closeAutoRenewalModal = useCallback(() => {
    setIsOpenAutoRenewalModal(false);
    // clear takerMarket after modal is closed
    clearTakerMarket();
  }, [clearTakerMarket]);

  const contextValue = useMemo(
    () => ({
      accordionValue,
      closeModal,
      isOpen,
      openModal,
      takerMarket,
      openDeleteScheduleModal,
      closeDeleteScheduleModal,
      isOpenDeleteScheduleModal,
      openAutoRenewalModal,
      closeAutoRenewalModal,
      isOpenAutoRenewalModal,
      offerModalReferrer,
    }),
    [
      accordionValue,
      closeModal,
      isOpen,
      openModal,
      takerMarket,
      openDeleteScheduleModal,
      closeDeleteScheduleModal,
      isOpenDeleteScheduleModal,
      openAutoRenewalModal,
      closeAutoRenewalModal,
      isOpenAutoRenewalModal,
      offerModalReferrer,
    ]
  );

  return <OfferModalContext.Provider value={contextValue}>{children}</OfferModalContext.Provider>;
};

const OfferModalComponent = () => {
  const { accordionValue, closeModal, isOpen, takerMarket, offerModalReferrer } = useOfferModalContext();

  return takerMarket ? (
    <OfferModal
      initialAccordionValue={accordionValue}
      onClose={closeModal}
      open={isOpen}
      takerMarket={takerMarket}
      referrer={offerModalReferrer}
    />
  ) : null;
};

const DeleteScheduleModalComponent = () => {
  const { isOpenDeleteScheduleModal, closeDeleteScheduleModal, takerMarket } = useOfferModalContext();

  return takerMarket ? (
    <DeleteScheduleModal
      onClose={closeDeleteScheduleModal}
      open={isOpenDeleteScheduleModal}
      takerMarket={takerMarket}
    />
  ) : null;
};

const AutoRenewalModalComponent = () => {
  const { isOpenAutoRenewalModal, closeAutoRenewalModal, takerMarket } = useOfferModalContext();

  return takerMarket ? (
    <AutoRenewalModal onClose={closeAutoRenewalModal} open={isOpenAutoRenewalModal} takerMarket={takerMarket} />
  ) : null;
};

export const OfferModalWithContext = ({ children }: { children: ReactNode }) => {
  return (
    <OfferModalProvider>
      <OfferModalComponent />
      <DeleteScheduleModalComponent />
      <AutoRenewalModalComponent />
      {children}
    </OfferModalProvider>
  );
};
